import { Component, Element, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import { isPlatform } from '@baloise/ds-core';

@Component({
  tag: 'bam-stage-multi',
  styleUrl: 'bam-stage-multi.scss',
  shadow: false,
})
export class BamStageMulti {
  @Element() el: HTMLElement;

  wrapper: HTMLElement;
  /**
   * PROPS
   * ======================================================
   *
   * controls
   */
  /**
   * PROPS
   * ======================================================
   *
   * controls
   */
  @Prop() controls: string;

  /*
   * boolean to dispaly interferer area
   */
  @Prop() hasInterferer: boolean = false;

  /*
   * Translation: Next Btn
   */
  @Prop() nextSlideLabel: string;

  /*
   * Translation: Prev Btn
   */
  @Prop() prevSlideLabel: string;

  /*
   * Translation: Stop Btn
   */
  @Prop() stopLabel: string;

  /*
   * Translation: Play Btn
   */
  @Prop() playLabel: string;

  /**
   * STATE
   * ======================================================
   *
   */
  @State() viewport: 'mobile' | 'tablet' | 'desktop' | 'highDefinition';
  @State() isTouchDevice: boolean = true;
  @State() prevSlide: number = 0;
  @State() activeSlide: number = 0;
  @State() playing: boolean = false;
  @State() autoplayDisabled: boolean = false;
  @State() hovered: boolean = false;
  @State() controlsDisabled: boolean = false;

  private slides: NodeListOf<HTMLElement>;
  private interferers: NodeListOf<HTMLElement>;
  private mobileHeadlines: NodeListOf<HTMLElement>;
  private timer: ReturnType<typeof setInterval>;

  @Listen('resize', { target: 'window' })
  async resizeHandler() {
    this.setViewPort();
  }

  @Watch('viewport')
  watchPropHandler(newViewport: string) {
    if (this.mobileHeadlines && (newViewport === 'mobile' || newViewport === 'tablet')) {
      this.setHeadlineHeight();
    }
  }

  /**
   * UTILS
   * ======================================================
   */
  setViewPort() {
    if (isPlatform('highDefinition') || isPlatform('widescreen') || isPlatform('fullhd')) {
      this.viewport = 'highDefinition';
      this.isTouchDevice = false;
    } else if (isPlatform('desktop')) {
      this.viewport = 'desktop';
      this.isTouchDevice = false;
    } else if (isPlatform('tablet')) {
      this.viewport = 'tablet';
      this.isTouchDevice = true;
    } else if (isPlatform('mobile')) {
      this.viewport = 'mobile';
      this.isTouchDevice = true;
    }
  }

  setHeadlineHeight = () => {
    let headlineHeight = 0;
    this.mobileHeadlines.forEach(headline => {
      const innerHeadlineHeight = headline.querySelector('bal-heading').clientHeight;
      if (innerHeadlineHeight > headlineHeight) {
        headlineHeight = innerHeadlineHeight;
      }
    });

    this.mobileHeadlines.forEach(headline => (headline.style.height = `${headlineHeight + 64}px`));
  };

  setAnimationClasses = (items: NodeListOf<HTMLElement>) => {
    items.forEach((item: HTMLElement) => {
      item.classList.remove('is-active');
      item.ariaHidden = 'true';
      item.tabIndex = -1;

      item.querySelectorAll('a').forEach(link => {
        link.ariaHidden = 'true';
        link.tabIndex = -1;
      });
    });
    items[this.activeSlide].classList.add('is-active');
    items[this.activeSlide].ariaHidden = 'false';
    items[this.activeSlide].tabIndex = 0;
    items[this.prevSlide].classList.add('fades-out');

    items[this.activeSlide].querySelectorAll('a').forEach(link => {
      link.ariaHidden = 'false';
      link.tabIndex = 0;
    });

    setTimeout(() => {
      items.forEach((item: HTMLElement) => {
        item.classList.remove('fades-out');
      });
      this.playing = false;
    }, 2000);
  };

  setSlides = () => {
    if (this.activeSlide >= this.slides.length - 1) {
      this.prevSlide = this.slides.length - 1;
      this.activeSlide = 0;
    } else {
      this.prevSlide = this.activeSlide;
      this.activeSlide += 1;
    }

    this.setAnimationClasses(this.slides);
    if (this.hasInterferer) {
      this.setAnimationClasses(this.interferers);
    }
  };

  // ######################################################## //
  startAutoPlay = () => {
    if (this.autoplayDisabled) {
      return;
    }

    if (!this.hovered || (this.isTouchDevice && !this.playing)) {
      this.timer = setInterval(() => {
        this.setSlides();
      }, 8000);
      this.playing = true;
    }
  };

  stopAutoPlay = () => {
    clearInterval(this.timer);
    this.playing = false;
  };

  /**
   * HANDLERS
   * ======================================================
   */
  handleClick = (index: number) => {
    this.controlsDisabled = true;
    this.stopAutoPlay();

    if (index !== this.activeSlide) {
      this.prevSlide = this.activeSlide;
      this.activeSlide = index;
      this.setAnimationClasses(this.slides);
      if (this.hasInterferer) {
        this.setAnimationClasses(this.interferers);
      }
    }

    setTimeout(() => {
      if (this.isTouchDevice && !this.playing) {
        this.startAutoPlay();
      }
      this.controlsDisabled = false;
    }, 3000);
  };

  handleMouseOver = () => {
    this.hovered = true;
    this.stopAutoPlay();
  };

  handleMouseLeave = () => {
    this.hovered = false;
    this.startAutoPlay();
  };

  togglePlay = () => {
    this.autoplayDisabled = !this.autoplayDisabled;

    if (this.autoplayDisabled) {
      this.stopAutoPlay();
    } else {
      this.startAutoPlay();
    }
  };

  /**
   * LIFECYCLE
   * ======================================================
   */
  connectedCallback() {
    this.setViewPort();
    this.slides = this.el.querySelectorAll('.is-slide');
  }

  componentDidLoad() {
    this.mobileHeadlines = this.el.querySelectorAll('.bam-stage-large__headline-mobile');
    if (this.isTouchDevice) {
      this.setHeadlineHeight();
    }

    this.slides.forEach(slide => {
      slide.ariaHidden = 'true';
      slide.tabIndex = -1;
      slide.querySelectorAll('a').forEach(link => {
        link.ariaHidden = 'true';
        link.tabIndex = -1;
      });
    });

    this.slides[this.activeSlide].classList.add('is-active');
    this.slides[this.activeSlide].ariaHidden = 'false';
    this.slides[this.activeSlide].tabIndex = 0;
    this.slides[this.activeSlide].querySelectorAll('a').forEach(link => {
      link.ariaHidden = 'false';
      link.tabIndex = 0;
    });

    if (this.hasInterferer) {
      this.interferers = this.el.querySelectorAll('bam-interferer');
      this.interferers[this.activeSlide].classList.add('is-active');
    };
    
    this.startAutoPlay();
  }

  disconnectedCallback() {
    this.stopAutoPlay();
  }

  render() {
    return (
      <Host class="bam-stage-multi">
        <div class="bam-stage-multi__wrapper" ref={el => (this.wrapper = el as HTMLElement)}>
          {/* Pause Button */}
          {/* <bal-button
            class="bam-stage-multi__pause-btn"
            size="small"
            color="primary"
            square={true}
            inverted={true}
            onClick={() => this.togglePlay()}
          >
            <svg aria-hidden="true" class={{ 'is-visible': !this.autoplayDisabled }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="10" height="10" fill="none">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M4 0C2.89543 0 2 0.895431 2 2V14C2 15.1046 2.89543 16 4 16C5.10457 16 6 15.1046 6 14V2C6 0.895431 5.10457 0 4 0ZM12 0C10.8954 0 10 0.895431 10 2V14C10 15.1046 10.8954 16 12 16C13.1046 16 14 15.1046 14 14V2C14 0.895431 13.1046 0 12 0Z"
                fill="white"
              />
            </svg>

            <svg aria-hidden="true" class={{ 'is-visible': this.autoplayDisabled }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="10" height="10" fill="none">
              <path
                d="M15.2841 9.46506L6.55367 15.7603C6.29893 15.9172 6.00883 16 5.71336 16C5.4179 16 5.1278 15.9172 4.87306 15.7603C4.62187 15.6245 4.40905 15.4219 4.25585 15.1729C4.10265 14.9238 4.01443 14.637 4.00001 14.341V1.7506C3.99891 1.4389 4.07984 1.13292 4.23387 0.866353C4.38791 0.599786 4.60909 0.382962 4.87306 0.23975C5.1278 0.0827688 5.4179 0 5.71336 0C6.00883 0 6.29893 0.0827688 6.55367 0.23975L15.2841 6.53494C15.5047 6.69241 15.6852 6.90404 15.81 7.1514C15.9348 7.39877 16 7.67437 16 7.95422C16 8.23406 15.9348 8.50967 15.81 8.75703C15.6852 9.0044 15.5047 9.21603 15.2841 9.37349V9.46506Z"
                fill="white"
              />
            </svg>
            <span class="visually-hidden">{this.autoplayDisabled ? this.playLabel : this.stopLabel}</span>
          </bal-button> */}
          {/* Pause Button End */}
          <slot></slot>
        </div>
        <div
          class={{
            'bam-stage-multi__controls': true,
            'has-interferer': this.hasInterferer,
          }}
          onMouseEnter={() => this.handleMouseOver()}
          onMouseLeave={() => this.handleMouseLeave()}
        >
          {this.isTouchDevice && (
            <bal-pagination
              disabled={this.controlsDisabled}
              page-range="1"
              value={this.activeSlide + 1}
              total-pages={this.slides.length}
              interface="small"
              onBalChange={e => this.handleClick(e.detail - 1)}
            ></bal-pagination>
          )}

          {this.viewport === 'desktop' && (
            <div class="flex flex-direction-row">
              <bal-button
                class="bam-stage-multi__control-desktop-small mr-x-small"
                square={true}
                color="primary"
                inverted
                icon="caret-left"
                disabled={this.controlsDisabled || this.activeSlide === 0}
                aria-label={this.prevSlideLabel}
                onClick={() => this.handleClick(this.activeSlide - 1)}
              ></bal-button>
              <bal-button color="primary" inverted size="small" class="bam-stage-multi__control-desktop-small-indicator">
                {this.controls.trim().split('; ')[this.activeSlide]}
              </bal-button>
              <bal-button
                class="bam-stage-multi__control-desktop-small ml-x-small"
                square={true}
                color="primary"
                inverted
                icon="caret-right"
                disabled={this.controlsDisabled || this.activeSlide === this.slides.length - 1}
                aria-label={this.nextSlideLabel}
                onClick={() => this.handleClick(this.activeSlide + 1)}
              ></bal-button>
            </div>
          )}

          {this.viewport === 'highDefinition' &&
            this.controls
              .trim()
              .split('; ')
              .map((control, index) => (
                <bal-button
                  class={{
                    'bam-stage-multi__control': true,
                    'is-active': index === this.activeSlide,
                  }}
                  color="primary"
                  inverted
                  size="small"
                  onClick={() => this.handleClick(index)}
                  disabled={this.controlsDisabled || this.playing}
                >
                  {control}
                </bal-button>
              ))}
        </div>
        <slot name="interferer"></slot>
      </Host>
    );
  }
}
